import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PRESETS } from "../../lib/tools/registry";
import { edition } from "../../lib/extensions/registry";

// The tools pinned to the shelf (docs/refactor-plan.md, Phase 2): kept with the account when signed
// in (/api/social/profile/tools), in this browser when signed out. `pins` is undefined while it
// loads, and null until they're first chosen (the library offers presets then). Every part of the
// page that shows them hears a change at once.
const KEY = "truechops.tools.pins", CHANGED = "truechops:pins";
const local = () => { try { const value = JSON.parse(localStorage.getItem(KEY)); return Array.isArray(value) ? value : null; } catch { return null; } };

export function useToolPins() {
  const user = useSelector((state) => state.realm.currentUser), loaded = useSelector((state) => state.realm.sessionLoaded);
  const [pins, setPins] = useState(undefined);
  useEffect(() => {
    if (!loaded) return undefined;
    let alive = true;
    if (!user) setPins(local());
    else fetch("/api/social/profile/tools", { credentials: "same-origin", cache: "no-store" }).then((response) => response.json().catch(() => null).then((data) => response.ok && data ? data : { pins: null }))
      .then((data) => { if (alive) setPins(Array.isArray(data.pins) ? data.pins : null); }).catch(() => { if (alive) setPins(null); });
    const heard = (event) => setPins(event.detail);
    window.addEventListener(CHANGED, heard);
    return () => { alive = false; window.removeEventListener(CHANGED, heard); };
  }, [user?.id, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useCallback((next) => {
    window.dispatchEvent(new CustomEvent(CHANGED, { detail: next }));
    if (!user) { try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* This visit only. */ } return; }
    fetch("/api/social/profile/tools", { method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pins: next }) }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = useCallback((id) => save((pins || []).includes(id) ? pins.filter((pin) => pin !== id) : [...(pins || []), id].slice(-12)), [pins, save]);
  // (An edition can start everyone on a preset, in place of asking.)
  return { pins: pins === null ? PRESETS.find((preset) => preset.id === edition().preset)?.tools || null : pins, save, toggle };
}

// Whether the shelf is out, and which tool it shows: the same on every part of the page, and kept
// in this browser.
const SHELF = "truechops.shelf", SHELF_CHANGED = "truechops:shelf";
const readShelf = () => { try { return { open: false, tool: null, ...JSON.parse(localStorage.getItem(SHELF) || "{}") }; } catch { return { open: false, tool: null }; } };
export function useShelf() {
  const [shelf, setShelf] = useState({ open: false, tool: null });
  useEffect(() => {
    setShelf(readShelf());
    const heard = (event) => setShelf(event.detail);
    window.addEventListener(SHELF_CHANGED, heard);
    return () => window.removeEventListener(SHELF_CHANGED, heard);
  }, []);
  const change = useCallback((changes) => {
    const next = { ...readShelf(), ...changes };
    try { localStorage.setItem(SHELF, JSON.stringify(next)); } catch { /* This visit only. */ }
    window.dispatchEvent(new CustomEvent(SHELF_CHANGED, { detail: next }));
  }, []);
  return [shelf, change];
}
